import { Nullable } from 'legacy/lut/classes/models/utils'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { Table } from 'legacy/lut/library/list'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'
import {
  changeIsLegacyDocument,
  changeIsSelectedDocumentChanged,
  selectIsQbrDocumentFinalized,
  selectQBRDocumentGlobalPartnerId,
  selectQBRDocumentPartnerName,
  selectQBRPartners,
  selectQBRRtm,
} from 'partner360/features/QBR'
import { selectQBREntryId } from 'partner360/features/QBR/selectors/selectQBREntryId'
import { IQBRPartner } from 'partner360/models/qbr/IQBRPartners'

import { useQbrSave } from '../../../hooks/useQbrSave'
import { NextButton } from '../../nextButton/NextButton'

import { QbrDeletePartnerFromDocumentDialog } from './components/qbrDeletePartnerFromDocumentDialog/QbrDeletePartnerFromDocumentDialog'
import { QBRErrorModal } from './components/qbrErrorModal/QBRErrorModal'
import { QbrSettingsForm } from './components/qbrSettingsForm'
import { SelectPartnersModal } from './components/selectPartnersModal/SelectPartnersModal'
import { getColumns } from './helpers/getColumns'
import { isShowSelectPartnersModalDialog } from './helpers/isShowSelectPartnersModalDialog'

import styles from './SettingsStep.module.scss'

interface ISettingsStepProps {
  isQbrLegacyDocumentMode?: boolean
  showListOfAllDocuments: () => void
}

export const SettingsStep: FC<ISettingsStepProps> = ({
  isQbrLegacyDocumentMode,
  showListOfAllDocuments,
}) => {
  const location = useLocation()
  const [itemToDelete, setItemToDelete] = useState<Nullable<IQBRPartner>>(null)
  const documentEntryId = useSelector(selectQBREntryId)
  const isFinalized = useSelector(selectIsQbrDocumentFinalized)
  const documentPartnerName = useSelector(selectQBRDocumentPartnerName)
  const globalPartnerId = useSelector(selectQBRDocumentGlobalPartnerId)

  const [isShowSelectPartnersModal, setIsIsShowSelectPartnersModal] = useState(
    isShowSelectPartnersModalDialog(location.pathname, documentEntryId)
  )
  const qbrPartners = useSelector(selectQBRPartners)
  const qbrRTM = useSelector(selectQBRRtm)
  const dispatch = useAppDispatch()

  const { onNextButtonClick, isSavingProgress, showErrorModal, onCloseErrorModal } = useQbrSave()

  const buttonText = useMemo(
    () =>
      qbrRTM === 'Distributor'
        ? TRANSLATION_KEYS.settings.addDistributors
        : TRANSLATION_KEYS.settings.addCommercials,
    [qbrRTM]
  )

  const onOpenPartnerPlan = useCallback((item: IQBRPartner) => {
    window.open(`/page/jbl-hpscoutso/pid/${item.PartnerID}`, '_blank')
  }, [])

  const columns = useMemo(
    () =>
      getColumns({
        setItemToDelete,
        onOpenPartnerPlan,
        isFinalized,
        globalPartnerId,
      }),
    [globalPartnerId, onOpenPartnerPlan, isFinalized]
  )

  const staticSource = useMemo(() => {
    const staticCrud = new Crud<IQBRPartner>({
      fetcher: new Static(qbrPartners),
      keyField: 'PartnerID',
      defaultPaging: {
        page: 0,
        pageSize: 5,
      },
    })
    staticCrud.removeQueries(true)
    return staticCrud
  }, [qbrPartners])

  const openAddPartnersModal = useCallback(() => {
    setIsIsShowSelectPartnersModal(true)
  }, [])

  const closeAddPartnersModal = useCallback(() => {
    setIsIsShowSelectPartnersModal(false)
  }, [])

  const closeAndGoBack = useCallback(() => {
    setIsIsShowSelectPartnersModal(false)

    if (!documentEntryId) {
      showListOfAllDocuments()
    }
  }, [documentEntryId, showListOfAllDocuments])

  const qbrSettingsDeleteVersionCloseDialog = useCallback(() => {
    setItemToDelete(null)
    dispatch(changeIsSelectedDocumentChanged(true))
  }, [dispatch])

  useEffect(() => {
    if (isQbrLegacyDocumentMode) {
      dispatch(changeIsLegacyDocument(true))
    }
  }, [isQbrLegacyDocumentMode, dispatch])

  return (
    <div>
      <div className={styles.contentRoot}>
        <RenderWhen condition={Boolean(documentPartnerName)}>
          <h3>{documentPartnerName}</h3>
        </RenderWhen>

        <QbrSettingsForm />

        <RenderWhen condition={!isFinalized && !globalPartnerId}>
          <Button className={styles.addPartnerButton} onClick={openAddPartnersModal}>
            <Translation text={buttonText} />
          </Button>
        </RenderWhen>

        <Table<IQBRPartner>
          hasTranslate
          pagination
          header
          source={staticSource}
          columns={columns}
        />
        <NextButton onClick={onNextButtonClick} />
        <SelectPartnersModal
          show={isShowSelectPartnersModal}
          onSubmit={closeAddPartnersModal}
          onClose={closeAndGoBack}
        />
        <QbrDeletePartnerFromDocumentDialog
          selectedPartner={itemToDelete}
          qbrSettingsData={qbrPartners}
          show={Boolean(itemToDelete)}
          closeDialog={qbrSettingsDeleteVersionCloseDialog}
        />
        <QBRErrorModal
          show={showErrorModal}
          onCloseButtonClick={onCloseErrorModal}
          isButtonDisabled={isSavingProgress}
        />
      </div>
    </div>
  )
}
